import { useCallback, useEffect, useState, useSyncExternalStore } from 'react';
import {
  clearInstallPrompt,
  getInstallPromptSnapshot,
  subscribeInstallPrompt,
} from './install-prompt-store';

/**
 * État de l'installabilité PWA :
 *  - `unavailable`  : navigateur non éligible (pas d'événement, pas en standalone) → rien à montrer.
 *  - `installable`  : `beforeinstallprompt` reçu → on peut déclencher `promptInstall()`.
 *  - `ios-manual`   : iOS Safari (pas de `beforeinstallprompt`) → afficher des instructions manuelles.
 *  - `installed`    : déjà installée / lancée en mode standalone → rien à montrer.
 */
export type InstallStatus = 'unavailable' | 'installable' | 'ios-manual' | 'installed';

export interface InstallPromptApi {
  status: InstallStatus;
  /** Déclenche la proposition native (no-op si aucun événement en attente). */
  promptInstall: () => Promise<void>;
}

/** `true` si l'app tourne déjà en mode autonome (installée ou lancée depuis l'écran d'accueil). */
function isStandalone(): boolean {
  if (typeof window === 'undefined') return false;
  const media =
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(display-mode: standalone)').matches;
  // iOS expose `navigator.standalone` (hors types DOM standard).
  const iosStandalone =
    typeof navigator !== 'undefined' &&
    (navigator as Navigator & { standalone?: boolean }).standalone === true;
  return media || iosStandalone;
}

/** `true` pour iOS/iPadOS Safari, qui ne supporte pas `beforeinstallprompt`. */
function isIosSafari(): boolean {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent;
  const isIos = /iphone|ipad|ipod/i.test(ua);
  // iPadOS 13+ se présente comme un Mac ; on le détecte via l'écran tactile.
  const isIpadOs =
    /macintosh/i.test(ua) &&
    typeof navigator.maxTouchPoints === 'number' &&
    navigator.maxTouchPoints > 1;
  if (!isIos && !isIpadOs) return false;
  // Exclut les navigateurs tiers qui embarquent leur propre chaîne (Chrome/Firefox iOS).
  return !/crios|fxios|edgios/i.test(ua);
}

/**
 * Détecte l'installabilité PWA et fournit une action d'installation.
 *
 * L'événement `beforeinstallprompt` n'est PAS écouté ici mais dans
 * [`install-prompt-store`](./install-prompt-store.ts), dont l'écouteur est posé à
 * l'import : Chromium l'émet souvent avant le premier montage React, et il ne se rejoue
 * jamais — un écouteur monté trop tard le perd pour toute la session (l'en-tête du store
 * détaille le constat). Le hook ne fait donc que LIRE ce store, et n'écoute lui-même que
 * ce qui se produit forcément après le montage : le passage en mode standalone.
 *
 * **SSR/test-safe** : tous les accès à `window`/`matchMedia`/`navigator` sont gardés. En
 * dev/test l'événement ne se produit pas → `status` reste `unavailable` (ou `installed`
 * si déjà en standalone), donc rien ne s'affiche.
 */
export function useInstallPrompt(): InstallPromptApi {
  const { deferred, installed: installedByEvent } = useSyncExternalStore(
    subscribeInstallPrompt,
    getInstallPromptSnapshot,
    getInstallPromptSnapshot,
  );
  // Mode autonome : lu au montage (l'app peut avoir été LANCÉE depuis l'écran d'accueil),
  // puis suivi ci-dessous. Contrairement à `beforeinstallprompt`, rien ne peut être
  // « manqué » ici — c'est un état, pas un événement unique.
  const [standalone, setStandalone] = useState<boolean>(() => isStandalone());

  useEffect(() => {
    if (typeof window === 'undefined') return;
    // Réagit au passage en mode standalone (installation acceptée dans un autre onglet).
    const media =
      typeof window.matchMedia === 'function'
        ? window.matchMedia('(display-mode: standalone)')
        : null;
    const onDisplayModeChange = (e: MediaQueryListEvent): void => {
      if (e.matches) setStandalone(true);
    };
    media?.addEventListener?.('change', onDisplayModeChange);
    return () => media?.removeEventListener?.('change', onDisplayModeChange);
  }, []);

  const promptInstall = useCallback(async (): Promise<void> => {
    if (!deferred) return;
    await deferred.prompt();
    // `userChoice` se résout que l'utilisateur accepte ou non ; dans les deux cas
    // l'événement différé est consommé et ne peut plus être rejoué.
    await deferred.userChoice;
    clearInstallPrompt();
  }, [deferred]);

  const installed = standalone || installedByEvent;
  const status: InstallStatus = installed
    ? 'installed'
    : deferred
      ? 'installable'
      : isIosSafari()
        ? 'ios-manual'
        : 'unavailable';

  return { status, promptInstall };
}
